import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth';
import { ORG_ADMIN_ROLE } from '@/auth/roles';
import { useRole } from '@/auth/use-role';
import { resolveEditAllowed } from '../resolve-edit-allowed';
import { canSeeOrderSetsTab } from '../settings-tabs';
import {
  useCreateDoctor,
  useDoctorSetupDetails,
  usePractitionerById,
  buildCreateDoctorBody,
} from '@/settings/services';
import {
  PractitionerEditShell,
  type PractitionerTabId,
} from './practitioner-edit-shell';
import { PractitionerPersonalTab } from './tabs/practitioner-personal-tab';
import { PractitionerAccountTab } from './tabs/practitioner-account-tab';
import { PractitionerProfessionalTab } from './tabs/practitioner-professional-tab';
import { PractitionerEducationalTab } from './tabs/practitioner-educational-tab';
import { PractitionerScheduleTab } from './tabs/practitioner-schedule-tab';
import { PractitionerPracticesTab } from './tabs/practitioner-practices-tab';
import { PractitionerBillingTab } from './tabs/practitioner-billing-tab';
import { MacroTab } from '../macro/macro-tab';
import { OrderSetsTab } from '../order-sets/order-sets-tab';
import {
  CreatePractitionerForm,
  EMPTY_CREATE_DRAFT,
  isCreatePractitionerDraftValid,
  type CreatePractitionerDraft,
} from './create-practitioner-form';
import {
  PractitionerSaveContext,
  type PractitionerSaveHandle,
  type PractitionerSaveContextValue,
} from './use-practitioner-save-context';

/**
 * Practitioner create/edit page (PROD-761 + PROD-762 + PROD-763 + PROD-764
 * + PROD-896 + PROD-897).
 *
 * Full-page route at `/settings/practitioner/:doctorSetupId` (edit) or
 * `/settings/practitioner/new` (create). Mirrors Flutter
 * `doctor_base_widget.dart` — 8 conditional tabs (or 2 if isNotBoarded).
 *
 * **Create mode** (PROD-896) renders a focused single-form Add Practitioner
 * shape (matches Flutter `add_doctor_widget.dart` STEP 1 — STEP 2 working
 * hours configured later from Settings → Schedule). On Send Invite:
 *   1. POST /api/Doctor via useCreateDoctor
 *   2. Invalidate ['settings', 'doctors-list']
 *   3. Navigate to /settings/practitioner/{newDoctorSetupId} (edit mode)
 *
 * **Edit mode** uses the per-tab save model — each tab owns its own draft
 * and mutation (Flutter `doctor_base_widget.dart:90` per-tab `isUnsaved`
 * flag + `Save{Tab}Event`). The page-level Save button (PROD-897) reads
 * from `PractitionerSaveContext` — the active tab registers its
 * `{ canSave, isPending, save, label }` handle and the chrome Save button
 * dispatches it. Tabs that haven't migrated to the registration pattern
 * leave the chrome Save disabled (no regression).
 */
export interface PractitionerEditPageProps {
  mode: 'create' | 'edit';
  /**
   * Account Settings mode (Flutter `isFromAccountSettings`, PROD-863 follow-up).
   * The logged-in practitioner edits their OWN record. Set by
   * `AccountSettingsRoute`, never by the admin list routes.
   */
  accountMode?: boolean;
  /**
   * In account mode the id comes from the auth user (`userData.id`), not the
   * `:doctorSetupId` route param. Ignored unless `accountMode` is true.
   */
  idOverride?: string;
}

export function PractitionerEditPage({
  mode,
  accountMode = false,
  idOverride,
}: PractitionerEditPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const params = useParams<{ doctorSetupId: string }>();
  // Origin-aware Back (Flutter `doPop` = Navigator.pop → returns to whatever
  // pushed this screen). Callers pass `state.from` when opening the edit page:
  // the Settings practitioners list sends `/settings/practitioner`, the
  // calendar provider-name tap sends `/` (back to the calendar). Falls back to
  // the practitioners list for deep-links / callers that don't set it, and to
  // `/` in account-settings self-edit mode.
  const backTo = (location.state as { from?: string } | null)?.from ?? null;
  const doctorSetupId =
    mode === 'edit' ? (accountMode ? idOverride : params.doctorSetupId) : undefined;

  const authUser = useAuthStore((s) => s.user);
  const orgId = authUser?.organizationId;
  const canEdit = usePermission(['Manage_Doctors'], { edit: true });
  const canAdd = usePermission(['Manage_Doctors'], { add: true });
  const canDelete = usePermission(['Manage_Doctors'], { delete: true });
  const canSeeBilling = usePermission(['Billing_Settings'], { view: true });
  // Archive lives on the Account tab (Flutter `doctor_account_tab.dart:458`):
  // existing record + Manage_Doctors delete, never in Account Settings
  // self-edit (Flutter renders the simplified layout there).
  const canArchive = mode === 'edit' && !accountMode && canDelete;
  // PROD-1817 — PA divergence, deliberate. In PM the Macro tab is an ORG-ADMIN
  // surface. In PA it is part of the DOCTOR's own self-edit set (PA's previous
  // `settings-tabs.ts` gated it on `!isNurse`), so account mode must also see
  // it — otherwise the port silently drops a tab PA doctors have today.
  // Account mode on THIS page is always a doctor: a nurse self-editing is
  // routed to StaffEditPage by `account-settings-route.tsx`.
  const canSeeMacro = authUser?.role === ORG_ADMIN_ROLE || accountMode;
  // PROD-2710 — physician-only, own settings only (see canSeeOrderSetsTab).
  const { mayOrder } = useRole();
  const canSeeOrderSets = canSeeOrderSetsTab({ accountMode, mayOrder });
  // Self-edit is always allowed in account mode regardless of Manage_Doctors —
  // see resolveEditAllowed (mirrors the Flutter `... || isFromAccountSettings`
  // save guard, doctor_base_widget.dart:297,338,372,413,444,476,533).
  const isAllowed = resolveEditAllowed({ mode, accountMode, canAdd, canEdit });

  // Doctor wrapper for Account + Schedule data. `userId` (the practitioner
  // GUID used by Personal/Professional/Educational) differs from
  // `doctorSetupId` (the doctor record GUID). Personal tab uses
  // `doctorSetupDetails.doctorSetupId` for the doctor-side userId.
  // For commit 1 we assume the route :doctorSetupId IS the userId for
  // Personal too — verified against the live cURL where both are the
  // same GUID for the test record.
  const { data: doctorWrapper, isLoading } = useDoctorSetupDetails(doctorSetupId);

  // Resolves the practitioner's `orgnizationId` (Flutter typo verbatim on
  // wire) for the shared MacroTab. The Macro tab requires this as a prop
  // (PROD-787 shared-page refactor lifted org-id resolution out of the
  // tab so the same tab can render under Staff too). Same cross-tab
  // cache pull pattern as Billing / Schedule.
  const { data: practitioner } = usePractitionerById(doctorSetupId);
  const macroOrgId = practitioner?.orgnizationId ?? null;

  // Boarded heuristic: if the doctor record has a primary speciality set,
  // they're past the onboarding gate (Flutter checks `isNotBoarded` against
  // a separate flag we don't have a hook for yet — approximate for now).
  const isNotBoarded = !!doctorWrapper && !doctorWrapper.doctorSetupDetail.speciality;

  // The tab can be named in navigation state (the Provider fees crumb returns to 'billing'); anything else opens Personal.
  const [activeTab, setActiveTab] = useState<PractitionerTabId>(() =>
    (location.state as { tab?: string } | null)?.tab === 'billing' ? 'billing' : 'personal',
  );

  // ── Create mode (PROD-896) ─────────────────────────────────────────────
  const [createDraft, setCreateDraft] =
    useState<CreatePractitionerDraft>(EMPTY_CREATE_DRAFT);
  const createMutation = useCreateDoctor();

  const handleCreate = useCallback(() => {
    if (!orgId || !isCreatePractitionerDraftValid(createDraft)) return;
    const body = buildCreateDoctorBody({
      organizationId: orgId,
      firstName: createDraft.firstName.trim(),
      lastName: createDraft.lastName.trim(),
      emailId: createDraft.emailId.trim(),
      mobileNumber: createDraft.mobileNumber,
      role: createDraft.role!,
      doctorSpecialities: createDraft.doctorSpecialities,
    });
    createMutation.mutate(body, {
      onSuccess: (resp) => {
        const fullName =
          `${createDraft.firstName.trim()} ${createDraft.lastName.trim()}`.trim();
        toast.success(`Invited ${fullName || 'practitioner'}`);
        // Caller owns invalidation (Samer pattern PR #323).
        queryClient.invalidateQueries({
          queryKey: ['settings', 'doctors-list'],
        });
        // Hand off to edit mode so the user can fill in the 8 tabs.
        // `resp.doctorId.id` is the server-issued `doctorSetupId` GUID —
        // curl-verified 2026-05-21 (see docs/CONVERSIONS/onboarding-
        // wizard-pr4/CONVERSION_NOTES.md §3, `AddDoctorResponse` interface
        // in use-create-doctor.ts:22-29). Not a client-generated id.
        navigate(`/settings/practitioner/${resp.doctorId.id}`);
      },
      onError: (err) =>
        toast.error(
          err instanceof Error
            ? err.message
            : 'Could not create practitioner.',
        ),
    });
  }, [createDraft, createMutation, navigate, orgId, queryClient]);

  // ── Edit mode (PROD-897) — page-level Save via tab registration ────────
  const [savedHandle, setSavedHandle] =
    useState<PractitionerSaveHandle | null>(null);
  const saveContextValue = useMemo<PractitionerSaveContextValue>(
    () => ({
      register: setSavedHandle,
      unregister: () => setSavedHandle(null),
    }),
    [],
  );

  // ── Shell prop assembly ────────────────────────────────────────────────
  const practitionerName = doctorWrapper
    ? `${doctorWrapper.doctorSetupDetail.firstName ?? ''} ${doctorWrapper.doctorSetupDetail.lastName ?? ''}`.trim() ||
      'Practitioner'
    : 'Practitioner';

  const isCreate = mode === 'create';
  const createValid = isCreatePractitionerDraftValid(createDraft);
  const createPending = createMutation.isPending;

  const saveLabel = isCreate
    ? createPending
      ? 'Sending…'
      : 'Send Invite'
    : savedHandle?.isPending
      ? 'Saving…'
      : (savedHandle?.label ?? 'Save');

  const saveDisabled = isCreate
    ? !isAllowed || !createValid || createPending || !orgId
    : !savedHandle || !savedHandle.canSave || savedHandle.isPending;

  const onSave = isCreate
    ? handleCreate
    : (savedHandle?.save ?? (() => undefined));

  return (
    <PractitionerSaveContext.Provider value={saveContextValue}>
      <PractitionerEditShell
        mode={mode}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isNotBoarded={isNotBoarded}
        canSeeBilling={canSeeBilling}
        canSeeMacro={canSeeMacro}
        canSeeOrderSets={canSeeOrderSets}
        practitionerName={practitionerName}
        photoUrl={doctorWrapper?.doctorSetupDetail.photoURL ?? null}
        accountMode={accountMode}
        onBack={() =>
          navigate(backTo ?? (accountMode ? '/' : '/settings/practitioner'))
        }
        saveLabel={saveLabel}
        onSave={onSave}
        saveDisabled={saveDisabled}
        isLoading={mode === 'edit' && isLoading && !doctorWrapper}
      >
        {isCreate ? (
          <CreatePractitionerForm
            draft={createDraft}
            onChange={setCreateDraft}
            organizationId={orgId}
            disabled={createPending || !isAllowed}
          />
        ) : !doctorSetupId ? null : activeTab === 'personal' ? (
          <PractitionerPersonalTab userId={doctorSetupId} canEdit={isAllowed} />
        ) : activeTab === 'account' ? (
          <PractitionerAccountTab
            doctorSetupId={doctorSetupId}
            canEdit={isAllowed}
            canArchive={canArchive}
            onArchived={() => navigate('/settings/practitioner')}
            accountMode={accountMode}
          />
        ) : activeTab === 'professional' ? (
          <PractitionerProfessionalTab doctorSetupId={doctorSetupId} canEdit={isAllowed} />
        ) : activeTab === 'educational' ? (
          <PractitionerEducationalTab doctorSetupId={doctorSetupId} canEdit={isAllowed} />
        ) : activeTab === 'schedule' ? (
          <PractitionerScheduleTab doctorSetupId={doctorSetupId} />
        ) : activeTab === 'practices' ? (
          <PractitionerPracticesTab doctorSetupId={doctorSetupId} />
        ) : activeTab === 'billing' ? (
          <PractitionerBillingTab doctorSetupId={doctorSetupId} />
        ) : activeTab === 'orderSets' ? (
          canSeeOrderSets ? <OrderSetsTab /> : null
        ) : (
          <MacroTab providerId={doctorSetupId} organizationId={macroOrgId} />
        )}
      </PractitionerEditShell>
    </PractitionerSaveContext.Provider>
  );
}
